import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import API_BASE_URL from '../config/api';
import { TONES, formatCurrency, formatDate, getBalance, getDueInfo, getPaidAmount, getStatusBadge, buildQueryString, PARTY_DETAIL_SORT_OPTIONS } from './financeUtils';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: 'Bearer ' + localStorage.getItem('token'),
});

const primaryButtonStyle = {
  padding: spacing.sm + ' ' + spacing.md,
  borderRadius: '0.5rem',
  border: 'none',
  backgroundColor: colors.primary[500],
  color: 'white',
  fontWeight: 700,
  cursor: 'pointer',
  fontSize: '0.85rem',
};

const secondaryButtonStyle = {
  padding: spacing.sm + ' ' + spacing.md,
  borderRadius: '0.5rem',
  border: '1px solid ' + colors.border,
  backgroundColor: 'white',
  color: colors.text.primary,
  fontWeight: 700,
  cursor: 'pointer',
  fontSize: '0.85rem',
};

const miniButtonStyle = {
  padding: spacing.xs + ' ' + spacing.sm,
  borderRadius: '0.375rem',
  border: '1px solid ' + colors.border,
  backgroundColor: 'white',
  color: colors.text.primary,
  fontWeight: 700,
  cursor: 'pointer',
  fontSize: '0.75rem',
};

// Compact action button used inside each invoice row.
const ActionChip = ({ onClick, disabled, tone, children }) => {
  const palette = tone ? TONES[tone] : null;
  return (
    <button
      type='button'
      disabled={disabled}
      onClick={onClick}
      style={{
        ...miniButtonStyle,
        border: '1px solid ' + (palette ? palette.border : colors.border),
        backgroundColor: palette ? palette.background : 'white',
        color: palette ? palette.color : colors.text.primary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
};

const toolbarInputStyle = {
  padding: spacing.sm + ' ' + spacing.md,
  border: '1px solid ' + colors.border,
  borderRadius: '0.5rem',
  fontSize: '0.85rem',
  backgroundColor: 'white',
  color: colors.text.primary,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  flex: '0 1 190px',
  minWidth: '150px',
};

// Distributor / store profile: contact details, money summary and the complete
// invoice history for that party.
function FinancePartyModal({
  config,
  partyId,
  refreshToken,
  onClose,
  onChanged,
  onEditParty,
  onEditInvoice,
  onRecordPayment,
  onAddInvoice,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyInvoiceId, setBusyInvoiceId] = useState(null);
  const [invoiceSearchInput, setInvoiceSearchInput] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceSort, setInvoiceSort] = useState('newest');

  // Debounce the invoice-number search so we do not refetch on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setInvoiceSearch(invoiceSearchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [invoiceSearchInput]);

  const fetchDetail = useCallback(async () => {
    if (!partyId) return;
    setLoading(true);
    setError('');
    try {
      const query = buildQueryString({ invoiceNumber: invoiceSearch, sort: invoiceSort });
      const response = await fetch(
        API_BASE_URL + '/api/admin/finance/' + config.partyEndpoint + '/' + partyId + query,
        { headers: authHeaders() }
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(body.message || 'Could not load ' + config.partyTitle.toLowerCase() + ' details');
        setData(null);
      } else {
        setData(body.data);
      }
    } catch (err) {
      setError('Connection error: ' + err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [config.partyEndpoint, config.partyTitle, partyId, invoiceSearch, invoiceSort]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail, refreshToken]);

  const notifyParent = (message, type) => {
    if (message === 'refresh') {
      onChanged('', 'refresh');
      return;
    }
    onChanged(message, type);
  };

  const togglePaymentStatus = async (invoice) => {
    const nextStatus = invoice.paymentStatus === 'paid' ? 'unpaid' : 'paid';
    if (nextStatus === 'unpaid' && !invoice.dueDate) {
      notifyParent('This invoice has no due date. Use Edit to set a due date before marking it unpaid.', 'error');
      return;
    }
    setBusyInvoiceId(invoice.id);
    try {
      const response = await fetch(
        API_BASE_URL + '/api/admin/finance/' + config.endpoint + '/' + invoice.id + '/payment-status',
        { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ paymentStatus: nextStatus }) }
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        notifyParent(body.message || 'Could not update payment status', 'error');
      } else {
        notifyParent(nextStatus === 'paid' ? 'Marked as Paid' : 'Marked as Unpaid', 'success');
        await fetchDetail();
        notifyParent('refresh', 'refresh');
      }
    } catch (err) {
      notifyParent('Connection error: ' + err.message, 'error');
    } finally {
      setBusyInvoiceId(null);
    }
  };

  const deleteInvoice = async (invoice) => {
    const confirmed = window.confirm(
      'Delete invoice ' + invoice.invoiceNumber + ' (' + formatCurrency(invoice.amount) + ')? This cannot be undone.'
    );
    if (!confirmed) return;

    setBusyInvoiceId(invoice.id);
    try {
      const response = await fetch(
        API_BASE_URL + '/api/admin/finance/' + config.endpoint + '/' + invoice.id,
        { method: 'DELETE', headers: authHeaders() }
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        notifyParent(body.message || 'Could not delete invoice', 'error');
      } else {
        notifyParent(config.invoiceTitle + ' invoice deleted', 'success');
        await fetchDetail();
        notifyParent('refresh', 'refresh');
      }
    } catch (err) {
      notifyParent('Connection error: ' + err.message, 'error');
    } finally {
      setBusyInvoiceId(null);
    }
  };

  const party = data ? data.party : null;
  const invoices = data ? data.invoices : [];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 55,
        padding: spacing.md,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
        onClick={(event) => event.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          boxShadow: shadows.xl,
          width: '100%',
          maxWidth: '860px',
          maxHeight: '92vh',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            padding: spacing.lg,
            borderBottom: '2px solid ' + colors.border,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: spacing.md,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, color: colors.text.secondary, letterSpacing: '0.08em' }}>
              {config.partyTitle.toUpperCase()}
            </p>
            <h3 style={{ margin: spacing.xs + ' 0 0', fontSize: '1.3rem', fontWeight: 900, color: colors.text.primary }}>
              {party ? party.name : 'Loading...'}
            </h3>
            {party ? (
              <div style={{ marginTop: spacing.xs, color: colors.text.secondary, fontSize: '0.85rem' }}>
                {party.contactPerson ? <span>{party.contactPerson}</span> : null}
                {party.contactPerson && party.phone ? <span> &middot; </span> : null}
                {party.phone ? <span>{party.phone}</span> : null}
                {party.email ? <span> &middot; {party.email}</span> : null}
                {party.creditDays ? <div>Credit period: {party.creditDays} days</div> : null}
                {party.address ? <div>{party.address}</div> : null}
                {party.notes ? <div style={{ fontStyle: 'italic' }}>{party.notes}</div> : null}
              </div>
            ) : null}
          </div>
          <button
            type='button'
            onClick={onClose}
            aria-label='Close'
            style={{ border: 'none', background: 'transparent', fontSize: '1.4rem', cursor: 'pointer', color: colors.text.secondary }}
          >
            &times;
          </button>
        </div>

        <div style={{ padding: spacing.lg }}>
          {loading ? (
            <p style={{ color: colors.text.secondary }}>Loading {config.partyTitle.toLowerCase()} details...</p>
          ) : error ? (
            <p style={{ color: colors.error, fontWeight: 600 }}>{error}</p>
          ) : (
            <React.Fragment>

              <div className='pd-party-totals'>
                {[
                  { label: config.totalLabel, value: party.totalAmount, tone: 'neutral' },
                  { label: config.paidLabel, value: party.totalPaid, tone: 'paid' },
                  { label: config.outstandingLabel, value: party.outstanding, tone: 'unpaid' },
                  { label: config.overdueLabel, value: party.overdue, tone: 'overdue' },
                  {
                    label: config.partialLabel,
                    value: party.partialAmount || 0,
                    tone: 'partial',
                    hint: (party.partialCount || 0) + ' invoice(s)',
                  },
                ].map((card) => (
                  <div
                    key={card.label}
                    style={{
                      padding: spacing.md,
                      borderRadius: '0.75rem',
                      backgroundColor: TONES[card.tone].background,
                      border: '1px solid ' + TONES[card.tone].border,
                    }}
                  >
                    <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, color: TONES[card.tone].color }}>{card.label}</p>
                    <p style={{ margin: spacing.xs + ' 0 0', fontSize: '1.05rem', fontWeight: 900, color: TONES[card.tone].color }}>
                      {formatCurrency(card.value)}
                    </p>
                    {card.hint ? (
                      <p style={{ margin: spacing.xs + ' 0 0', fontSize: '0.68rem', color: colors.text.secondary }}>{card.hint}</p>
                    ) : null}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginTop: spacing.lg }}>
                <button type='button' onClick={() => onAddInvoice(party)} style={primaryButtonStyle}>
                  + New {config.invoiceTitle} Invoice
                </button>
                <button type='button' onClick={() => onEditParty(party)} style={secondaryButtonStyle}>
                  Edit {config.partyTitle}
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing.md, flexWrap: 'wrap', marginTop: spacing.lg }}>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: colors.text.primary }}>
                  Invoices ({invoices.length}
                  {party.invoiceCount !== invoices.length ? ' of ' + party.invoiceCount : ''})
                  {party.latestInvoiceDate ? (
                    <span style={{ fontWeight: 500, fontSize: '0.8rem', color: colors.text.secondary }}>
                      {' '}&middot; latest {formatDate(party.latestInvoiceDate)}
                    </span>
                  ) : null}
                </h4>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', flex: '1 1 320px', justifyContent: 'flex-end' }}>
                  <input
                    type='text'
                    value={invoiceSearchInput}
                    onChange={(event) => setInvoiceSearchInput(event.target.value)}
                    placeholder='Search invoice number'
                    style={toolbarInputStyle}
                    aria-label='Search invoice number'
                  />
                  <select
                    value={invoiceSort}
                    onChange={(event) => setInvoiceSort(event.target.value)}
                    style={toolbarInputStyle}
                    aria-label='Sort invoices'
                  >
                    {PARTY_DETAIL_SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {invoices.length === 0 ? (
                <p style={{ color: colors.text.secondary }}>
                  {invoiceSearch ? 'No invoices match "' + invoiceSearch + '".' : config.emptyInvoices}
                </p>
              ) : (
                <div style={{ display: 'grid', gap: spacing.sm, marginTop: spacing.md }}>

                  {invoices.map((invoice) => {
                    const badge = getStatusBadge(invoice);
                    const due = getDueInfo(invoice);
                    const busy = busyInvoiceId === invoice.id;
                    const invoicePaid = getPaidAmount(invoice);
                    const balance = getBalance(invoice);
                    return (
                      <div
                        key={invoice.id}
                        style={{
                          border: '1px solid ' + colors.border,
                          borderRadius: '0.75rem',
                          padding: spacing.md,
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: spacing.md,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          backgroundColor: invoice.isOverdue ? '#fffbfb' : 'white',
                        }}
                      >
                        <div style={{ minWidth: '170px' }}>
                          <p style={{ margin: 0, fontWeight: 800, color: colors.text.primary }}>
                            {invoice.invoiceNumber}
                            {invoice.paymentMethod === 'cash' ? (
                              <span
                                style={{
                                  marginLeft: '6px',
                                  padding: '2px 8px',
                                  borderRadius: '999px',
                                  fontSize: '0.62rem',
                                  fontWeight: 800,
                                  letterSpacing: '0.05em',
                                  backgroundColor: TONES.paid.background,
                                  color: TONES.paid.color,
                                  border: '1px solid ' + TONES.paid.border,
                                }}
                              >
                                CASH
                              </span>
                            ) : null}
                          </p>
                          <p style={{ margin: spacing.xs + ' 0 0', fontSize: '0.78rem', color: colors.text.secondary }}>
                            {config.dateLabel}: {formatDate(invoice[config.dateField])}
                            {invoice.dueDate ? ' | Due: ' + formatDate(invoice.dueDate) : ''}
                          </p>
                        </div>
                        <div style={{ minWidth: '150px' }}>
                          <p style={{ margin: 0, fontWeight: 800, color: colors.text.primary }}>{formatCurrency(invoice.amount)}</p>
                          <p style={{ margin: spacing.xs + ' 0 0', fontSize: '0.78rem', color: colors.text.secondary }}>
                            Paid <span style={{ color: invoicePaid > 0 ? TONES.paid.color : colors.text.secondary, fontWeight: 700 }}>
                              {formatCurrency(invoicePaid)}
                            </span>
                            {' | Balance '}
                            <span style={{ color: balance > 0 ? TONES.unpaid.color : TONES.paid.color, fontWeight: 800 }}>
                              {formatCurrency(balance)}
                            </span>
                          </p>
                          <p style={{ margin: spacing.xs + ' 0 0', fontSize: '0.78rem', color: TONES[due.tone].color, fontWeight: 600 }}>
                            {due.label}
                          </p>
                        </div>
                        <span
                          style={{
                            padding: '2px 10px',
                            borderRadius: '999px',
                            fontSize: '0.68rem',
                            fontWeight: 800,
                            letterSpacing: '0.05em',
                            backgroundColor: TONES[badge.tone].background,
                            color: TONES[badge.tone].color,
                            border: '1px solid ' + TONES[badge.tone].border,
                          }}
                        >
                          {badge.label}
                        </span>
                        <div style={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap' }}>
                          {balance > 0 ? (
                            <ActionChip onClick={() => onRecordPayment(invoice)} disabled={busy} tone='partial'>
                              Record Payment
                            </ActionChip>
                          ) : null}
                          {balance <= 0 && invoicePaid > 0 ? (
                            <ActionChip onClick={() => togglePaymentStatus(invoice)} disabled={busy} tone='unpaid'>
                              Mark Unpaid
                            </ActionChip>
                          ) : null}
                          {balance > 0 ? (
                            <ActionChip onClick={() => togglePaymentStatus(invoice)} disabled={busy} tone='paid'>
                              Mark Paid
                            </ActionChip>
                          ) : null}
                          <ActionChip onClick={() => onEditInvoice(invoice)} disabled={busy}>
                            Edit
                          </ActionChip>
                          <ActionChip onClick={() => deleteInvoice(invoice)} disabled={busy} tone='overdue'>
                            Delete
                          </ActionChip>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default FinancePartyModal;




